/**
 * Extension Bridge — sends license key to the xReplAI Chrome extension
 * via chrome.runtime.sendMessage (externally_connectable API).
 *
 * The extension must declare our origin in its manifest externally_connectable.
 * The chrome.runtime global is available on web pages in Chrome when the
 * extension is installed and has externally_connectable matching the origin.
 */

// Published Chrome Web Store ID (stable) + local dev ID (for testing unpacked builds)
const EXTENSION_IDS = [
  'aeemmefmpkgmokecemoedojagcecabmj',  // production (Chrome Web Store)
  'bbcpjmkhphmhadpafjlkopphpcndnkgm',  // local dev (unpacked)
];
const STORAGE_KEY = 'xrepl_auth_source';

/* ── Session helpers ─────────────────────────────────────────────── */

/** Mark that the current session originated from the extension popup. 
 *  Uses localStorage to survive external redirects (e.g., Stripe checkout).
 */
export function markExtensionSource(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'extension');
  } catch { /* SSR / private browsing */ }
}

/** Check whether the current session was initiated by the extension. */
export function isExtensionSource(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'extension';
  } catch {
    return false;
  }
}

/** Clear the extension source marker (call after successful transfer). */
export function clearExtensionSource(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

/* ── Messaging ───────────────────────────────────────────────────── */

/**
 * Attempt to send a license key to the xReplAI extension.
 * Returns true if the extension acknowledged successfully.
 * Returns false (no throw) if extension is not installed, messaging
 * is unavailable, or the extension rejects the message.
 */
export async function sendLicenseToExtension(
  licenseKey: string,
  plan: string,
): Promise<boolean> {
  if (!licenseKey) return false;

  const chr = (globalThis as any).chrome;
  if (!chr?.runtime?.sendMessage) return false;

  // Try each known extension ID (production first, then dev)
  for (const extId of EXTENSION_IDS) {
    try {
      const sent = await new Promise<boolean>((resolve) => {
        chr.runtime.sendMessage(
          extId,
          { type: 'LICENSE_KEY_TRANSFER', licenseKey, plan },
          (response: any) => {
            if (chr.runtime.lastError) {
              // Extension not found at this ID — try next
              resolve(false);
              return;
            }
            resolve(response?.success === true);
          },
        );
      });
      if (sent) return true;
    } catch {
      // Try next ID
    }
  }

  console.warn('Failed to send license to extension (no reachable extension)');
  return false;
}

/**
 * Convenience: send license to extension only if the session was initiated
 * by the extension. Returns true if license was sent successfully.
 */
export async function trySendLicenseToExtension(
  licenseKey: string,
  plan: string,
): Promise<boolean> {
  if (!isExtensionSource()) return false;
  const sent = await sendLicenseToExtension(licenseKey, plan);
  if (sent) {
    clearExtensionSource();
  }
  return sent;
}

/**
 * Notify the extension that the user's account has been deleted.
 * The extension should clear all stored data (license key, settings).
 */
export async function notifyAccountDeleted(): Promise<boolean> {
  const chr = (globalThis as any).chrome;
  if (!chr?.runtime?.sendMessage) return false;

  for (const extId of EXTENSION_IDS) {
    try {
      const sent = await new Promise<boolean>((resolve) => {
        chr.runtime.sendMessage(
          extId,
          { type: 'ACCOUNT_DELETED' },
          (response: any) => {
            if (chr.runtime.lastError) {
              resolve(false);
              return;
            }
            resolve(response?.success === true);
          },
        );
      });
      if (sent) return true;
    } catch {
      // Try next ID
    }
  }

  return false;
}
